import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock } from 'lucide-react';
import { tone, displayRating, overallHeadline, deriveSummary, orderResults } from '../utils/csiReport';

/**
 * CSICoachingReport: the shared CSI result presentation used both on the user's
 * result page and in the admin/coach response view (same component, so the two
 * stay identical). Layout: a personal greeting, a chart summary (overall-score
 * donut + a per-area score bar for each focus area), the single Next Action,
 * then two columns -- "What you're doing well" (strengths) and "Where to focus
 * next" (developing/weak areas) -- each listing its areas with the stored
 * description + coaching message / recommended action. A column hides when its
 * set is empty (all-strong hides focus; all-weak hides strengths) and the
 * remaining one spans full width. Stored coaching content is shown verbatim.
 *
 * Props: result {results, overall, overallPercent, submittedOn}, isDarkMode,
 *        firstName (optional; the client's name when an admin views it),
 *        celebrating (optional; while the score-reveal overlay is animating the
 *        donut + commentary are left blank here so the celebration's own values
 *        fly into these anchors and hand off seamlessly — no double / mismatched
 *        score showing through the overlay).
 */

// "green" is the positive rating band — kept as the semantic success color
// (not brand primary blue), since "blue" already means a distinct rating
// here ("Needs More Information").
const PILL_LIGHT = { green: 'bg-success-tint text-success', yellow: 'bg-amber-100 text-amber-800', red: 'bg-rose-100 text-rose-800', blue: 'bg-blue-100 text-blue-800' };
const PILL_DARK = { green: 'bg-success/20 text-success', yellow: 'bg-amber-900/40 text-amber-300', red: 'bg-rose-900/40 text-rose-300', blue: 'bg-blue-900/40 text-blue-300' };
// Score-band fills for the donut arc and bars (matches the assessment slider).
const BAND_HEX = { green: '#16a34a', yellow: '#ca8a04', red: '#dc2626', blue: '#94a3b8' };
const DONUT_C = 2 * Math.PI * 76; // circumference of the r=76 donut

export default function CSICoachingReport({ result, isDarkMode, firstName, celebrating }) {
    const navigate = useNavigate();
    const rows = orderResults(result.results || []);
    const overall = result.overall || {};
    const overallPct = result.overallPercent;
    const { nextAction } = deriveSummary(rows, overall);
    // Freemium gate: the backend strips the per-area recommendations + Next
    // Action for free / in-trial clients and flags recommendationsLocked, so
    // we show an upgrade prompt instead. Admin/coach views are never gated.
    const locked = !!result.recommendationsLocked;

    const heading = isDarkMode ? 'text-slate-100' : 'text-slate-900';
    const sub = isDarkMode ? 'text-slate-400' : 'text-slate-600';
    const body = isDarkMode ? 'text-slate-300' : 'text-slate-700';
    const card = isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200';
    const trackBg = isDarkMode ? '#334155' : '#eceae3';
    const pill = (rating) => (isDarkMode ? PILL_DARK : PILL_LIGHT)[tone(rating)];
    const hex = (rating) => BAND_HEX[tone(rating)];

    const strengths = rows.filter(r => tone(r.rating) === 'green');
    const focus = rows.filter(r => ['yellow', 'red'].includes(tone(r.rating)));
    const bothShown = strengths.length > 0 && focus.length > 0;

    const wellBox = isDarkMode ? 'bg-primary-900/20 border-primary-800' : 'bg-primary-50 border-primary-200';
    const focusBox = isDarkMode ? 'bg-rose-900/20 border-rose-800' : 'bg-rose-50 border-rose-200';
    const nextBox = isDarkMode ? 'bg-blue-900/20 border-blue-800' : 'bg-blue-50 border-blue-200';

    const Item = ({ r, i }) => (
        <div className={i > 0 ? `pt-3 mt-3 border-t ${isDarkMode ? 'border-white/10' : 'border-black/5'}` : ''}>
            <div className="flex items-center gap-2 mb-1">
                <span className={`font-semibold ${heading}`}>{r.focusAreaName}</span>
                <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${pill(r.rating)}`}>{displayRating(r.rating)}</span>
            </div>
            {r.description && <p className={`text-xs ${sub} mb-1 leading-snug`}>{r.description}</p>}
            {r.coachingMessage && <p className={`text-sm ${body} mb-1 leading-snug`}>{r.coachingMessage}</p>}
            {r.recommendedAction && <p className={`text-sm ${body} leading-snug`}>▸ {r.recommendedAction}</p>}
        </div>
    );

    // Free / in-trial users: a clean locked upsell in place of the detailed
    // recommendations — lock + heading + copy + "Upgrade to unlock" CTA.
    const LockedRecommendations = () => (
        <div className={`rounded-2xl border p-8 md:p-10 flex flex-col items-center text-center ${card}`}>
            <div className={`w-12 h-12 rounded-full border flex items-center justify-center mb-3 ${nextBox}`}>
                <Lock size={22} className={isDarkMode ? 'text-blue-400' : 'text-blue-500'} />
            </div>
            <h3 className={`text-lg font-bold ${heading}`}>Unlock your detailed recommendations</h3>
            <p className={`mt-1.5 text-sm max-w-md ${sub}`}>
                {/*See exactly what you’re doing well and your personalized next steps for each area — available on any paid plan.*/}
                See exactly what you’re doing well and your personalized next steps for each area - available on plans that include detailed recommendations.
            </p>
            <button onClick={() => navigate('/membership/plans')} className="mt-4 rounded-xl bg-primary-600 px-6 py-2.5 font-semibold text-white shadow-lg shadow-primary-600/30 hover:bg-primary-700 transition-colors">
                Upgrade to unlock →
            </button>
        </div>
    );

    return (
        <div className="space-y-5">
            {/* Greeting */}
            <div>
                <h1 className={`text-2xl md:text-3xl font-extrabold ${heading}`}>
                    {firstName ? `Hi ${firstName} 👋` : 'Your Career Success snapshot 👋'}
                </h1>
                <p className={`mt-1 ${sub}`}>
                    This is a starting point, not a grade. Here’s where you’re already strong and where a little focus will go furthest.
                </p>
            </div>

            {/* Chart summary: overall donut + per-area bars */}
            <div className={`rounded-2xl border p-5 md:p-6 ${card}`}>
                <div className="flex flex-col sm:flex-row sm:items-center gap-5">
                    <div data-flip="donut" className="shrink-0 flex flex-col items-center">
                        <div className="relative" style={{ width: 118, height: 118 }}>
                            <svg width="118" height="118" viewBox="0 0 200 200">
                                <circle cx="100" cy="100" r="76" fill="none" stroke={trackBg} strokeWidth="20" />
                                {!celebrating && overallPct != null && (
                                    <circle cx="100" cy="100" r="76" fill="none" stroke={hex(overall.rating)} strokeWidth="20"
                                        strokeLinecap="round" strokeDasharray={`${(overallPct / 100) * DONUT_C} ${DONUT_C}`}
                                        transform="rotate(-90 100 100)" />
                                )}
                            </svg>
                            <div className="absolute inset-0 flex flex-col items-center justify-center">
                                {!celebrating && (
                                    <div className={`text-2xl font-extrabold leading-none ${heading}`}>
                                        {overallPct != null ? Math.round(overallPct) : '—'}
                                        <span className={`text-sm font-semibold ${sub}`}>/100</span>
                                    </div>
                                )}
                            </div>
                        </div>
                        {!celebrating && overall.rating && (
                            <span className={`mt-2 text-[11px] font-semibold px-2.5 py-1 rounded-full ${pill(overall.rating)}`}>
                                {displayRating(overall.rating)}
                            </span>
                        )}
                    </div>

                    <div className="flex-1 min-w-0 w-full space-y-2">
                        {rows.map(r => {
                            const pct = r.percent != null ? Math.round(r.percent) : null;
                            return (
                                <div key={r.focusAreaId} className="grid items-center gap-2" style={{ gridTemplateColumns: '84px 1fr auto' }}>
                                    <span className={`text-xs font-semibold truncate ${heading}`}>{r.focusAreaName}</span>
                                    <div className={`relative h-3 rounded-full overflow-hidden ${isDarkMode ? 'bg-slate-700' : 'bg-slate-100'}`}>
                                        {!celebrating && pct != null && <div className="absolute inset-y-0 left-0 rounded-full" style={{ width: `${pct}%`, background: hex(r.rating) }} />}
                                    </div>
                                    <span className={`text-xs font-bold tabular-nums text-right ${sub}`} style={{ minWidth: 20 }}>{!celebrating && pct != null ? pct : '—'}</span>
                                </div>
                            );
                        })}
                    </div>
                </div>

                <div data-flip="commentary" className={celebrating ? 'opacity-0' : ''}>
                    <p className={`text-base font-bold mt-4 ${heading}`}>{overallHeadline(overall.rating)}</p>
                    {overall.coachingMessage && <p className={`text-sm mt-1 ${body}`}>{overall.coachingMessage}</p>}
                </div>
            </div>

            {/* Detailed recommendations — gated for free / in-trial users. */}
            {locked ? <LockedRecommendations /> : (
                <>
                    {/* Next Action */}
                    {nextAction && nextAction !== '—' && (
                        <div className={`rounded-2xl border p-5 ${nextBox}`}>
                            <h2 className={`font-bold mb-1 ${heading}`}>Next Action</h2>
                            <p className={`text-sm ${body}`}>{nextAction}</p>
                        </div>
                    )}

                    {/* Strengths | Focus (a column hides when empty; the other spans full width) */}
                    {(strengths.length > 0 || focus.length > 0) && (
                        <div className={`grid grid-cols-1 gap-4 ${bothShown ? 'md:grid-cols-2' : ''}`}>
                            {strengths.length > 0 && (
                                <section className={`rounded-2xl border p-5 ${wellBox}`}>
                                    <h2 className={`text-lg font-bold mb-3 ${heading}`}>What you’re doing well 💪</h2>
                                    {strengths.map((r, i) => <Item key={r.focusAreaId} r={r} i={i} />)}
                                </section>
                            )}
                            {focus.length > 0 && (
                                <section className={`rounded-2xl border p-5 ${focusBox}`}>
                                    <h2 className={`text-lg font-bold mb-3 ${heading}`}>Where to focus next 🚀</h2>
                                    {focus.map((r, i) => <Item key={r.focusAreaId} r={r} i={i} />)}
                                </section>
                            )}
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
